/*
  Keyboard activation for the product-card controls.

  shop-wishlist, shop-compare and quick-view are custom elements the THEME defines and they all
  genuinely work - the heart writes to localStorage and /pages/wishlist reads it back, compare
  opens its own view, quick-view opens a modal. They were simply unreachable without a mouse,
  because a custom element is not a button and nothing gave them a tab stop.

  The markup now carries role="button" and tabindex="0", so this only has to make Enter and Space
  do what a real button would. It dispatches the SAME click the elements already listen for rather
  than reimplementing any behaviour, so there is one code path and no second source of truth.

  Delegated from the document: cards arrive from section rendering, filtering and quick-view, so
  binding per element would miss everything added after load.
*/
(function () {
  'use strict';

  var SELECTOR = 'shop-wishlist, shop-compare, quick-view';

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var el = e.target;
    if (!el || typeof el.closest !== 'function') return;
    var ctrl = el.closest(SELECTOR);
    if (!ctrl) return;
    // Let a real control inside the card handle its own key (a link, a form button).
    if (el !== ctrl && el.matches('a[href], button, input, select, textarea')) return;
    e.preventDefault();          // Space would otherwise scroll the page
    ctrl.click();
  });

  /*
    These buttons are revealed on card hover, and several themes keep them visually hidden until
    then. A keyboard user must be able to see what they have focused, so focus is treated as
    hover for the purpose of revealing them. Scoped to :focus-visible so a mouse click does not
    pin them open.
  */
  var style = document.createElement('style');
  style.textContent =
    'product-item:focus-within .item-btn,' +
    'shop-wishlist:focus-visible, shop-compare:focus-visible, quick-view:focus-visible' +
    '{opacity:1 !important;visibility:visible !important;pointer-events:auto !important;}';
  if (document.head) document.head.appendChild(style);

  /*
    F-024. These are toggles, and their accessible name did not say which way. The two
    `alo_tooltip-content` spans are display:block with opacity:0, which hides them from the eye but
    NOT from the accessibility tree, so the control offered "add to wishlist" and "remove from
    wishlist" simultaneously. The markup now marks those spans aria-hidden, which leaves the
    aria-label as the name - and a static label is still wrong, because it says "add" for an item
    that is already saved.

    So the STATE travels as aria-pressed instead of by swapping the label, which is the standard
    toggle pattern and avoids a second copy of the two strings living in JavaScript.

    Synced with a MutationObserver on `class` rather than from the click, because the `added` class is
    set by elements.js AFTER its own handler runs; observing the attribute means no timing guess and
    it also catches cards that arrive later from filtering, section rendering or quick view, and the
    initial state that ShopStorage applies on load.
  */
  var TOGGLES = 'shop-wishlist, shop-compare';
  function syncPressed(el) {
    /* The observer below sees EVERY class change on the page - swiper alone rewrites slide classes
       constantly - so this must confirm the element is actually one of the toggles before touching
       it. Without the guard it would stamp aria-pressed onto unrelated elements. */
    if (!el || !el.matches || !el.matches(TOGGLES)) return;
    el.setAttribute('aria-pressed', el.classList.contains('added') ? 'true' : 'false');
  }
  function syncAll(root) {
    var list = (root || document).querySelectorAll ? (root || document).querySelectorAll(TOGGLES) : [];
    for (var i = 0; i < list.length; i++) {
      list[i].setAttribute('aria-pressed', list[i].classList.contains('added') ? 'true' : 'false');
    }
  }
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'attributes') { syncPressed(r.target); continue; }
        for (var j = 0; j < r.addedNodes.length; j++) {
          var n = r.addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(TOGGLES)) syncPressed(n);
          syncAll(n);
        }
      }
    }).observe(document.documentElement, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['class'],
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { syncAll(); });
  else syncAll();

  /*
    Cart and mini-cart: checkout stays inactive until the terms box is ticked (theme setting
    show_terms_conditions; elements.js action-condition). A tap on the inactive button did nothing at
    all, and on the Arabic storefront the box had no label either (fixed in locales/ar.json,
    2026-09-14). The tap now brings the box into view, focuses it and outlines it briefly, so the
    customer sees what is missing. Nothing is ticked on their behalf.
  */
  var nudgeStyle = document.createElement('style');
  nudgeStyle.textContent = '.drv-terms-nudge{outline:2px solid #c62f38;outline-offset:4px;border-radius:6px;transition:outline-color .2s}';
  if (document.head) document.head.appendChild(nudgeStyle);
  document.addEventListener('click', function (e) {
    var act = e.target && typeof e.target.closest === 'function' && e.target.closest('action-condition .action.disabled');
    if (!act) return;
    var box = act.closest('action-condition').querySelector('input[type="checkbox"]');
    if (!box || box.checked) return;
    var wrap = box.closest('.alo__terms-conditions') || box.parentNode;
    wrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
    try { box.focus({ preventScroll: true }); } catch (err) { box.focus(); }
    wrap.classList.remove('drv-terms-nudge'); void wrap.offsetWidth; wrap.classList.add('drv-terms-nudge');
    setTimeout(function () { wrap.classList.remove('drv-terms-nudge'); }, 2400);
  }, true);
})();
